import StickyTableResponsive from '../../components/common/StickyTableResponsive';
import SortableTh from '../../components/common/SortableTh';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminBulkPriceAdjustPanel from '../../components/admin/AdminBulkPriceAdjustPanel';
import AdminFieldHelp from '../../components/admin/AdminFieldHelp';
import { isMonetaryUnit } from '../../utils/adminBulkPriceAdjust';
import { ITI_VARIABLE_HELP } from '../../utils/adminHelpContent';
import { useTableSort } from '../../hooks/useTableSort';
import { Link, NavLink, Navigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import {
  AppAlert,
  AppButton,
  AppInput,
  AppSpinner,
} from '../../components/ui';
import chiffrageSiteService from '../../services/chiffrageSiteService';
import ItiSecteursActiviteAdminSection from '../../components/iti/ItiSecteursActiviteAdminSection';
import ItiTypesSiteAdminSection from '../../components/iti/ItiTypesSiteAdminSection';
import ItiFonctionnalitesAdminSection from '../../components/iti/ItiFonctionnalitesAdminSection';
import { notify } from '../../components/common/AppToast';
import { ROUTES } from '../../config/routes';
import '../../styles/admin-nav.scss';

const VARIABLE_SORT_COLUMNS = {
  categorie: { type: 'string', getValue: (row) => row.category },
  libelle: { type: 'string', getValue: (row) => row.label },
  unite: { type: 'string', getValue: (row) => row.unit },
  valeur: { type: 'number', getValue: (row) => row.value ?? 0 },
};

const SECTIONS = [
  {
    id: 'variables',
    label: 'Variables de calcul',
    icon: 'mdi:calculator-variant-outline',
    hint: 'Coûts horaires, temps et tarifs',
  },
  {
    id: 'secteurs-activite',
    label: 'Secteurs d\'activité',
    icon: 'mdi:tag-multiple-outline',
    hint: 'Secteurs proposés dans les filtres sites web',
  },
  {
    id: 'types-site',
    label: 'Types de site',
    icon: 'mdi:web',
    hint: 'Types de site et fonctionnalités associées',
  },
  {
    id: 'fonctionnalites',
    label: 'Fonctionnalités',
    icon: 'mdi:puzzle-outline',
    hint: 'Fonctionnalités proposées sur les sites',
  },
];

const ItiAdminPage = () => {
  const { section = 'variables' } = useParams();
  const validSection = SECTIONS.some((s) => s.id === section) ? section : 'variables';
  const currentMeta = SECTIONS.find((s) => s.id === validSection);

  const [definitions, setDefinitions] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const { sortedRows: sortedDefinitions, sort: sortDefinitions, toggleSort: toggleSortDefinitions, getSortIcon: getSortIconDefinitions } = useTableSort(definitions, VARIABLE_SORT_COLUMNS);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await chiffrageSiteService.getAdminConfig();
      setDefinitions(data.variable_definitions ?? []);
      setCategories(data.variable_categories ?? {});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleVariableChange = (key, value) => {
    setDefinitions((prev) => prev.map((def) => (
      def.key === key ? { ...def, value: Number(value) || 0 } : def
    )));
  };

  const handleSaveVariables = async () => {
    setSaving(true);
    setError(null);
    try {
      const variables = {};
      definitions.forEach((def) => {
        variables[def.key] = def.value;
      });
      const data = await chiffrageSiteService.saveAdminConfig({ variables });
      setDefinitions(data.variable_definitions ?? []);
      notify.success('Variables enregistrées');
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const bulkPriceItems = useMemo(
    () => definitions
      .filter((def) => isMonetaryUnit(def.unit))
      .map((def) => ({
        id: def.key,
        groupLabel: def.label,
        groupSublabel: categories[def.category] ?? def.category,
        value: def.value ?? 0,
        unit: def.unit ?? '€',
      })),
    [definitions, categories],
  );

  const handleBulkPriceApply = async (updates) => {
    setSaving(true);
    setError(null);
    try {
      const byKey = Object.fromEntries(updates.map((u) => [u.id, u.newValue]));
      const nextDefinitions = definitions.map((def) => (
        byKey[def.key] != null ? { ...def, value: byKey[def.key] } : def
      ));
      const variables = {};
      nextDefinitions.forEach((def) => {
        variables[def.key] = def.value;
      });
      const data = await chiffrageSiteService.saveAdminConfig({ variables });
      setDefinitions(data.variable_definitions ?? []);
      notify.success(`${updates.length} tarif${updates.length > 1 ? 's' : ''} mis à jour`);
    } catch (err) {
      notify.error(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const renderVariables = () => (
    <>
      <AdminBulkPriceAdjustPanel
        title="Augmenter les tarifs en groupe"
        description="Coûts horaires, forfaits, abonnements et autres montants en euros. Les durées en heures ne sont pas modifiées."
        items={bulkPriceItems}
        onApply={handleBulkPriceApply}
        disabled={saving}
      />
      <div className="card admin-data-card">
        <div className="card-header d-flex align-items-center justify-content-between gap-2">
          <span>Variables ({definitions.length})</span>
          <AppButton size="sm" variant="success" loading={saving} loadingLabel="Enregistrement…" onClick={handleSaveVariables}>
            Enregistrer les modifications
          </AppButton>
        </div>
        <StickyTableResponsive embedded style={{ maxHeight: 520 }}>
          <table className="table table-sm table-hover mb-0">
            <thead>
              <tr>
                <SortableTh sortKey="categorie" label="Catégorie" sort={sortDefinitions} onSort={toggleSortDefinitions} getSortIcon={getSortIconDefinitions} />
                <SortableTh sortKey="libelle" label="Libellé" sort={sortDefinitions} onSort={toggleSortDefinitions} getSortIcon={getSortIconDefinitions} />
                <SortableTh sortKey="unite" label="Unité" sort={sortDefinitions} onSort={toggleSortDefinitions} getSortIcon={getSortIconDefinitions} />
                <SortableTh sortKey="valeur" label="Tarif" style={{ width: 180 }} sort={sortDefinitions} onSort={toggleSortDefinitions} getSortIcon={getSortIconDefinitions} />
              </tr>
            </thead>
            <tbody>
              {sortedDefinitions.map((field) => (
                <tr key={field.key}>
                  <td className="text-secondary-light">{categories[field.category] ?? field.category}</td>
                  <td className="fw-medium">
                    <span className="admin-table-label">
                      {field.label}
                      {ITI_VARIABLE_HELP[field.key] && (
                        <AdminFieldHelp
                          help={ITI_VARIABLE_HELP[field.key].help}
                          helpExample={ITI_VARIABLE_HELP[field.key].helpExample}
                        />
                      )}
                    </span>
                  </td>
                  <td className="text-sm text-secondary-light">{field.unit ?? '—'}</td>
                  <td>
                    {isMonetaryUnit(field.unit) ? (
                      <div className="input-group input-group-sm">
                        <AppInput
                          type="number"
                          step="any"
                          min="0"
                          value={field.value ?? 0}
                          onChange={(e) => handleVariableChange(field.key, e.target.value)}
                        />
                        <span className="input-group-text">€</span>
                      </div>
                    ) : (
                      <AppInput
                        type="number"
                        step="any"
                        min="0"
                        size="sm"
                        value={field.value ?? 0}
                        onChange={(e) => handleVariableChange(field.key, e.target.value)}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </StickyTableResponsive>
      </div>
    </>
  );

  const renderContent = () => {
    if (loading && validSection !== 'secteurs-activite' && validSection !== 'types-site' && validSection !== 'fonctionnalites') {
      return <AppSpinner />;
    }
    if (validSection === 'secteurs-activite') return <ItiSecteursActiviteAdminSection />;
    if (validSection === 'types-site') return <ItiTypesSiteAdminSection />;
    if (validSection === 'fonctionnalites') return <ItiFonctionnalitesAdminSection />;
    return renderVariables();
  };

  if (section !== validSection) {
    return <Navigate to={ROUTES.iti.adminSection(validSection)} replace />;
  }

  return (
    <MasterLayout>
      <Breadcrumb
        title="Administration itiConseil"
        subtitle={
          validSection === 'secteurs-activite'
            ? 'Référentiel secteurs d\'activité'
            : validSection === 'types-site'
              ? 'Référentiel types de site'
              : validSection === 'fonctionnalites'
                ? 'Référentiel fonctionnalités'
                : 'Paramètres du chiffrage site'
        }
        actions={(
          <AppButton as={Link} to={ROUTES.iti.chiffrageSite} size="sm" variant="outline-secondary">
            Retour chiffrage site
          </AppButton>
        )}
      />

      {error && <AppAlert>{error}</AppAlert>}

      <div className="row g-4">
        <div className="col-lg-3">
          <div className="card admin-nav-card admin-nav-card--iti kkp-admin-nav-card">
            <div className="card-body p-0">
              <div className="list-group list-group-flush">
                {SECTIONS.map((s) => (
                  <NavLink
                    key={s.id}
                    to={ROUTES.iti.adminSection(s.id)}
                    className={({ isActive }) => `list-group-item list-group-item-action d-flex align-items-start gap-2 border-0 ${isActive ? 'active' : ''}`}
                  >
                    <Icon icon={s.icon} className="mt-1 flex-shrink-0" width={20} />
                    <span>
                      <span className="d-block fw-semibold">{s.label}</span>
                      <small className={`d-block ${validSection === s.id ? '' : 'text-secondary-light'}`}>{s.hint}</small>
                    </span>
                  </NavLink>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-9">
          <div className="card">
            <div className="card-header bg-base d-flex align-items-center gap-2">
              <Icon icon={currentMeta?.icon} width={22} />
              <h6 className="mb-0 fw-semibold">{currentMeta?.label}</h6>
            </div>
            <div className="card-body">{renderContent()}</div>
          </div>
        </div>
      </div>
    </MasterLayout>
  );
};

export default ItiAdminPage;
